import { type ReactNode, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import type { Creator } from '@/types';
import { useUpdateCreator } from '../hooks/use-creator-mutations';
import { CreatorEditFields, type EditFormState } from './creator-edit-fields';

const EMAIL_RE = /^([^\s@]+@[^\s@]+\.[^\s@]+|no)$/;
type Props = { creator: Creator; onSave: () => void; onCancel: () => void };

function init(l: Creator): EditFormState {
  return {
    email: l.email,
    notes: l.notes,
    category: l.category,
    creator_type: l.creator_type,
    sex: l.sex,
    status: l.status,
  };
}

function payloadFor(
  form: EditFormState,
  canEditStatus: boolean
): Partial<Creator> {
  if (canEditStatus) return form;
  return {
    email: form.email,
    notes: form.notes,
    category: form.category,
    creator_type: form.creator_type,
    sex: form.sex,
  };
}

function ActionBar({
  onCancel,
  onSave,
  pending,
}: {
  onCancel: () => void;
  onSave: () => void;
  pending: boolean;
}): ReactNode {
  return (
    <div className="flex justify-end gap-sm">
      <Button variant="ghost" size="sm" onClick={onCancel}>
        Cancelar
      </Button>
      <Button size="sm" onClick={onSave} disabled={pending}>
        Guardar
      </Button>
    </div>
  );
}

export function CreatorEditForm({
  creator,
  onSave,
  onCancel,
}: Props): ReactNode {
  const { isAdmin } = useAuth();
  const [form, setForm] = useState<EditFormState>(init(creator));
  const update = useUpdateCreator();
  const ok = !form.email || EMAIL_RE.test(form.email);
  const save = (): void => {
    if (!ok) return;
    const data = payloadFor(form, isAdmin);
    update.mutate({ id: creator.id, data }, { onSuccess: onSave });
  };
  return (
    <div className="space-y-base">
      <CreatorEditFields form={form} setForm={setForm} emailValid={ok} />
      <ActionBar onCancel={onCancel} onSave={save} pending={update.isPending} />
    </div>
  );
}
